import { lazy, Suspense, useEffect } from 'react'
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { trackPageView } from '@/lib/analytics'
import { ThemeProvider } from '@/theme/ThemeProvider'
import { AuthProvider, useAuth } from '@/auth/AuthProvider'
import { DiaryProvider } from '@/diary/DiaryProvider'
import { ToastProvider } from '@/components/ui/Toast'
import { AppShell } from '@/components/AppShell'
import { FullScreenLoader } from '@/components/FullScreenLoader'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { SignIn } from '@/routes/SignIn'
import { Gate } from '@/routes/Gate'

const lazyRoute = <T extends object>(loader: () => Promise<T>, key: keyof T) =>
  lazy(() => loader().then((m) => ({ default: m[key] as unknown as React.ComponentType })))

const Diary = lazyRoute(() => import('@/routes/Diary'), 'Diary')
const Log = lazyRoute(() => import('@/routes/Log'), 'Log')
const Meal = lazyRoute(() => import('@/routes/Meal'), 'Meal')
const SavedFoods = lazyRoute(() => import('@/routes/SavedFoods'), 'SavedFoods')
const ScanHistory = lazyRoute(() => import('@/routes/ScanHistory'), 'ScanHistory')
const Recipes = lazyRoute(() => import('@/routes/Recipes'), 'Recipes')
const ImportedRecipe = lazyRoute(() => import('@/routes/ImportedRecipe'), 'ImportedRecipe')
const RecipeEditor = lazyRoute(() => import('@/routes/RecipeEditor'), 'RecipeEditor')
const RecipeChat = lazyRoute(() => import('@/routes/RecipeChat'), 'RecipeChat')
const Progress = lazyRoute(() => import('@/routes/Progress'), 'Progress')
const Settings = lazyRoute(() => import('@/routes/Settings'), 'Settings')
const GlycemicTargets = lazyRoute(() => import('@/routes/GlycemicTargets'), 'GlycemicTargets')
const OptionalNutrients = lazyRoute(() => import('@/routes/OptionalNutrients'), 'OptionalNutrients')
const HealthProfile = lazyRoute(() => import('@/routes/HealthProfile'), 'HealthProfile')
const Weight = lazyRoute(() => import('@/routes/Weight'), 'Weight')
const Glucose = lazyRoute(() => import('@/routes/Glucose'), 'Glucose')
const Water = lazyRoute(() => import('@/routes/Water'), 'Water')
const BloodPressure = lazyRoute(() => import('@/routes/BloodPressure'), 'BloodPressure')

function PublicOnly() {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (user) {
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from || '/diary'} replace />
  }
  return <Outlet />
}

function RequireAuth() {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
  return <Outlet />
}

function RequirePremium() {
  const { t } = useTranslation()
  const { user, loading, access, profileMissing } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
  if (profileMissing) return <Navigate to="/upgrade" replace />
  if (access === 'checking') return <FullScreenLoader label={t('webapp.paywall.checking')} />
  if (access === 'denied' || access === 'unverified') return <Navigate to="/upgrade" replace />
  return (
    <DiaryProvider>
      <AppShell>
        <Suspense fallback={<FullScreenLoader />}>
          <Outlet />
        </Suspense>
      </AppShell>
    </DiaryProvider>
  )
}

function RootLayout() {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname)
  }, [location.pathname])
  return <Outlet />
}

// Data router so editors can block in-app navigation (useBlocker) while they
// hold unsaved changes. Providers that must wrap the router live in App().
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route element={<PublicOnly />}>
        <Route path="/sign-in" element={<SignIn />} />
      </Route>
      <Route element={<RequireAuth />}>
        <Route path="/upgrade" element={<Gate />} />
      </Route>
      <Route element={<RequirePremium />}>
        <Route path="/diary" element={<Diary />} />
        <Route path="/log" element={<Log />} />
        <Route path="/meal/:mealId" element={<Meal />} />
        <Route path="/saved-foods" element={<SavedFoods />} />
        <Route path="/history" element={<ScanHistory />} />
        <Route path="/recipes" element={<Recipes />} />
        <Route path="/recipes/new" element={<RecipeEditor />} />
        <Route path="/recipes/:id" element={<ImportedRecipe />} />
        <Route path="/recipes/:id/edit" element={<RecipeEditor />} />
        <Route path="/recipes/:id/chat" element={<RecipeChat />} />
        <Route path="/progress" element={<Progress />} />
        <Route path="/weight" element={<Weight />} />
        <Route path="/glucose" element={<Glucose />} />
        <Route path="/water" element={<Water />} />
        <Route path="/blood-pressure" element={<BloodPressure />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/targets" element={<GlycemicTargets />} />
        <Route path="/settings/nutrients" element={<OptionalNutrients />} />
        <Route path="/settings/health-profile" element={<HealthProfile />} />
      </Route>
      <Route path="/" element={<Navigate to="/diary" replace />} />
      <Route path="*" element={<Navigate to="/diary" replace />} />
    </Route>,
  ),
)

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
